/* ==========================================================================
   Rental Period Selector — Web Components (MIETEN / KAUFEN)
   ========================================================================== */

/**
 * Format a price in cents using the shop's money format.
 * Falls back to a simple euro format if moneyFormat is unavailable.
 */
function formatMoney(cents) {
  const format = window.themeVariables?.settings?.moneyFormat || '{{amount}} €';
  const amount = (cents / 100).toFixed(2);
  const amountWithComma = amount.replace('.', ',');
  const amountNoDecimals = Math.round(cents / 100).toString();
  const amountNoDecimalsWithComma = amountNoDecimals;

  return format
    .replace('{{amount_with_comma_separator}}', amountWithComma.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.'))
    .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.'))
    .replace('{{amount_with_apostrophe_separator}}', amount.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1'"))
    .replace('{{amount_no_decimals_with_space_separator}}', amountNoDecimals.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 '))
    .replace('{{amount_no_decimals}}', amountNoDecimals)
    .replace('{{amount}}', amount.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,'));
}

/* --------------------------------------------------------------------------
   Helpers — date formatting & business-day buffer
   -------------------------------------------------------------------------- */

/**
 * Format a Date object as "YYYY-MM-DD".
 */
function toISODate(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return yyyy + '-' + mm + '-' + dd;
}

/**
 * Starting the day after `endDate`, iterate calendar days and collect them
 * as unavailable ISO strings. Only Mon–Fri count toward `businessDays`;
 * weekends are still added as unavailable but don't advance the counter.
 */
function getBusinessDayBuffer(endDate, businessDays) {
  const dates = [];
  if (businessDays <= 0) return dates;

  let counted = 0;
  const cursor = new Date(endDate);
  cursor.setHours(0, 0, 0, 0);

  while (counted < businessDays) {
    cursor.setDate(cursor.getDate() + 1);
    dates.push(toISODate(cursor));
    const dow = cursor.getDay(); // 0=Sun, 6=Sat
    if (dow !== 0 && dow !== 6) counted++;
  }
  return dates;
}

/* --------------------------------------------------------------------------
   RentalPeriodSelector (absorbs former RentalBuyButtons)
   -------------------------------------------------------------------------- */

class RentalPeriodSelector extends HTMLElement {
  // Tracks whether the user has interacted with period cards.
  // On first page load this is false → cards are unchecked.
  // After a card click triggers a variant-picker re-render, this is true
  // → the server-rendered checked state is preserved.
  static _hasBeenInteracted = false;

  connectedCallback() {
    // Defer initialization so child elements are fully parsed
    requestAnimationFrame(() => this._init());
  }

  _init() {
    this._variantData = this._parseVariantData();
    this._cards = this.querySelectorAll('input[name*="option"][data-rental-period]');
    this._formId = this.getAttribute('form-id');
    this._form = null;
    this._formListenerAttached = false;
    this._rentalPosition = this.getAttribute('data-rental-position');
    this._rentButtonText = this.getAttribute('data-rent-button-text') || 'JETZT MIETEN';
    this._rentButtonDisabledText = this.getAttribute('data-rent-button-disabled-text') || 'WÄHLE EINEN ZEITRAUM';
    this._inCartText = this.getAttribute('data-in-cart-text') || 'BEREITS IM WARENKORB';

    // Cart awareness — each physical item is unique single-stock, so it may
    // appear in the cart at most once (any duration / dates / buy option).
    this._cartVariantIds = new Set();
    this._inCart = false;

    // Mode containers
    this._mietenContainer = this.querySelector('[data-mode="mieten"]');
    this._kaufenContainer = this.querySelector('[data-mode="kaufen"]');

    // Summary elements
    this._summaryText = this.querySelector('[data-summary-text]');
    this._savingsBadge = this.querySelector('[data-savings-badge]');
    this._totalPrice = this.querySelector('[data-total-price]');
    this._buyPriceEl = this.querySelector('[data-buy-price]');

    // Rental form & CTA
    this._rentalForm = this.querySelector('product-form form') || this.querySelector('form');
    this._rentalFormVariantInput = this._rentalForm ? this._rentalForm.querySelector('input[name="id"]') : null;
    this._ctaBtn = this.querySelector('[data-rent-button]');

    this._dateSelected = false;

    // --- Event listeners ---

    // Clicking KAUFEN buy button → add buy variant to cart
    this._buyBtn = this.querySelector('[data-buy-button]');
    if (this._buyBtn) {
      this._buyButtonText = this._buyBtn.textContent.trim();
      this._buyBtn.addEventListener('click', () => this._executeBuy());
    }

    // Period card selection
    this._cards.forEach(input => {
      input.addEventListener('change', this._onPeriodChange.bind(this));
    });

    // Calendar date selected event
    this.addEventListener('rental-calendar:date-selected', this._onDateSelected.bind(this));

    // CTA click
    if (this._ctaBtn) {
      this._ctaBtn.addEventListener('click', this._onRentClick.bind(this));
    }

    // Resolve product form (may render after this block in DOM)
    this._resolveProductForm();

    // Initialize display state
    this._initializeState();

    // Reflect current cart contents (disable CTAs if this item is already in
    // the cart) and keep in sync as the cart changes.
    document.addEventListener('cart:change', this._onCartChange.bind(this));
    this._refreshCartFromNetwork();
  }

  /**
   * Lazily resolve the main product form (rendered by buy_buttons block).
   */
  _resolveProductForm() {
    if (this._form) return this._form;
    this._form = document.getElementById(this._formId);
    if (this._form && !this._formListenerAttached) {
      this._form.addEventListener('variant:change', this._onVariantChange.bind(this));
      this._formListenerAttached = true;
    }
    return this._form;
  }

  /**
   * Set initial UI state. On first page load, uncheck all cards so nothing
   * is pre-selected. On re-renders (after user interaction), preserve the
   * server-rendered checked state.
   */
  _initializeState() {
    if (!RentalPeriodSelector._hasBeenInteracted) {
      // First load — clear any server-set checked state
      this._cards.forEach(input => { input.checked = false; });
    }

    this._updatePriceDisplay();
    this._updateSummary();
    this._updateBuyPrice();

    const checkedCard = this.querySelector('input[data-rental-period]:checked');
    if (checkedCard) {
      const days = parseInt(checkedCard.dataset.rentalPeriod, 10);
      const calendar = this.querySelector('rental-calendar');
      if (calendar) {
        calendar.show();
        calendar.setPeriodDays(days);
      }
      this._updateTotal();
      this._setCTAState(false);
    }
  }

  _parseVariantData() {
    const script = this.querySelector('script[data-rental-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return [];
    }
  }

  /* --- Period Card Change --- */

  _onPeriodChange(event) {
    RentalPeriodSelector._hasBeenInteracted = true;
    const days = parseInt(event.target.dataset.rentalPeriod, 10);

    // Show calendar
    const calendar = this.querySelector('rental-calendar');
    if (calendar) {
      calendar.show();
      calendar.setPeriodDays(days);
    }

    // Update prices and summary
    this._updatePriceDisplay();
    this._updateTotal();
    this._updateSummary();

    // CTA stays disabled until a date is selected
    if (!this._dateSelected) {
      this._setCTAState(false);
    }
  }

  /* --- Calendar Date Selected --- */

  _onDateSelected() {
    this._dateSelected = true;
    this._setCTAState(true);
  }

  /* --- CTA State --- */

  _setCTAState(enabled) {
    if (!this._ctaBtn) return;
    // Already in the cart wins over every other state.
    if (this._inCart) {
      this._ctaBtn.disabled = true;
      this._ctaBtn.textContent = this._inCartText;
      return;
    }
    if (enabled) {
      this._ctaBtn.disabled = false;
      this._ctaBtn.textContent = this._rentButtonText;
    } else {
      this._ctaBtn.disabled = true;
      this._ctaBtn.textContent = this._rentButtonDisabledText;
    }
  }

  /* --- Rent Click --- */

  _onRentClick(event) {
    event.preventDefault();

    if (this._isSameItemInCart()) {
      this._notifyAlreadyInCart();
      return;
    }

    const calendar = this.querySelector('rental-calendar');
    const startInput = calendar ? calendar.querySelector('input[name="properties[Rental Start]"]') : null;

    if (!startInput || !startInput.value) {
      const existing = this.querySelector('.rental-date-error');
      if (existing) existing.remove();

      const msg = document.createElement('p');
      msg.className = 'rental-date-error';
      msg.textContent = 'Bitte wähle ein Startdatum im Kalender aus.';
      this._ctaBtn.insertAdjacentElement('beforebegin', msg);
      setTimeout(() => msg.remove(), 4000);
      return;
    }

    // Sync variant ID to rental form
    this._syncRentalFormVariant();

    // Enable calendar inputs so they submit
    if (calendar) calendar.enableInputs();

    // Submit the rental form
    if (this._rentalForm) this._rentalForm.requestSubmit();
  }

  /* --- Buy (KAUFEN) — direct Shopify AJAX --- */

  _executeBuy() {
    if (this._isSameItemInCart()) {
      this._notifyAlreadyInCart();
      return;
    }

    this._resolveProductForm();
    const selectedOther = this._getSelectedOtherOptions();
    const buyVariant = this._variantData.find(v => {
      if (!v.isBuy) return false;
      for (const [pos, valId] of Object.entries(selectedOther)) {
        if (v.otherOptions && String(v.otherOptions[pos]) !== String(valId)) return false;
      }
      return true;
    });

    if (!buyVariant) return;

    // Collect bundled sections (e.g. cart-drawer) the same way the theme does
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    const rootPath = window.Shopify?.routes?.root || '/';
    fetch(rootPath + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [{ id: buyVariant.id, quantity: 1 }],
        sections: sectionsToBundle.join(',')
      })
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(err => {
          document.dispatchEvent(new CustomEvent('toast:show', {
            detail: { message: err.message || 'Error adding to cart', tone: 'error' }
          }));
        });
      }
      return response.json().then(responseJson => {
        // Merge cart state with sections from the add response
        return fetch(rootPath + 'cart.js').then(r => r.json()).then(cartContent => {
          cartContent.sections = responseJson.sections || {};
          return cartContent;
        });
      }).then(cartContent => {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent }
        }));

        if (window.themeVariables?.settings?.cartType === 'page' ||
            window.themeVariables?.settings?.pageType === 'cart') {
          window.location.href = rootPath + 'cart';
        }
      });
    })
    .catch(() => {
      document.dispatchEvent(new CustomEvent('toast:show', {
        detail: { message: 'Error adding to cart', tone: 'error' }
      }));
    });
  }

  /* --- Cart awareness: one of each unique item --- */

  /**
   * React to the theme's cart:change event. Uses the cart payload when the
   * event carries one, otherwise re-fetches /cart.js.
   */
  _onCartChange(event) {
    const cart = event.detail?.cart;
    if (cart && Array.isArray(cart.items)) {
      this._setCartVariantIds(cart.items);
      this._applyInCartState();
    } else {
      this._refreshCartFromNetwork();
    }
  }

  /**
   * Fetch the current cart and refresh the in-cart state. Fails open: a cart
   * lookup error never blocks the customer from adding to cart.
   */
  _refreshCartFromNetwork() {
    const rootPath = window.Shopify?.routes?.root || '/';
    fetch(rootPath + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : null))
      .then(cart => {
        if (cart && Array.isArray(cart.items)) {
          this._setCartVariantIds(cart.items);
          this._applyInCartState();
        }
      })
      .catch(() => { /* fail open */ });
  }

  _setCartVariantIds(items) {
    this._cartVariantIds = new Set(items.map(i => i.variant_id || i.id));
  }

  /**
   * Variant IDs that represent THIS product in the currently selected size —
   * every rental duration plus the buy option. _variantData only holds this
   * product's variants, so this set uniquely identifies the same physical item
   * (product + size), ignoring duration and rent-vs-buy. For products without
   * a size option, selectedOther is empty and this returns every variant.
   */
  _sameItemVariantIds() {
    const selectedOther = this._getSelectedOtherOptions();
    return this._variantData
      .filter(v => {
        for (const [pos, valId] of Object.entries(selectedOther)) {
          if (v.otherOptions && String(v.otherOptions[pos]) !== String(valId)) return false;
        }
        return true;
      })
      .map(v => v.id);
  }

  _isSameItemInCart() {
    if (!this._cartVariantIds || this._cartVariantIds.size === 0) return false;
    return this._sameItemVariantIds().some(id => this._cartVariantIds.has(id));
  }

  /**
   * Disable the rent CTA and buy button (showing the "already in cart" label)
   * when this product+size is in the cart; restore them otherwise.
   */
  _applyInCartState() {
    this._inCart = this._isSameItemInCart();
    this.classList.toggle('is-in-cart', this._inCart);

    if (this._buyBtn) {
      this._buyBtn.disabled = this._inCart;
      this._buyBtn.textContent = this._inCart ? this._inCartText : this._buyButtonText;
    }

    // _setCTAState honours this._inCart, so pass the date-selection state and
    // let it decide the final label.
    this._setCTAState(this._dateSelected);
  }

  _notifyAlreadyInCart() {
    document.dispatchEvent(new CustomEvent('toast:show', {
      detail: { message: 'Dieses Piece ist bereits in deinem Warenkorb.' }
    }));
  }

  /* --- Variant Change (e.g. size) --- */

  _onVariantChange(event) {
    if (event.detail?.variant && this._rentalFormVariantInput) {
      this._rentalFormVariantInput.value = event.detail.variant.id;
    }
    this._updatePriceDisplay();
    this._updateTotal();
    this._updateSummary();
    this._updateBuyPrice();

    // Update calendar availability based on selected size/variant
    const calendar = this.querySelector('rental-calendar');
    if (calendar && event.detail?.variant) {
      const variant = event.detail.variant;
      // Find the non-rental option value (e.g. size name)
      const rentalPos = parseInt(this._rentalPosition, 10);
      let variantName = null;
      for (let i = 0; i < variant.options.length; i++) {
        if (i + 1 !== rentalPos) {
          variantName = variant.options[i];
          break;
        }
      }
      calendar.setVariantFilter(variantName);
    }

    // Selected size changed → the set of "same item" variants changed too.
    this._applyInCartState();
  }

  _syncRentalFormVariant() {
    const checkedCard = this.querySelector('input[data-rental-period]:checked');
    if (!checkedCard || !this._rentalFormVariantInput) return;

    const days = parseInt(checkedCard.dataset.rentalPeriod, 10);
    const selectedOther = this._getSelectedOtherOptions();

    const variant = this._variantData.find(v => {
      if (v.isBuy || v.days !== days) return false;
      for (const [pos, valId] of Object.entries(selectedOther)) {
        if (v.otherOptions && String(v.otherOptions[pos]) !== String(valId)) return false;
      }
      return true;
    });

    if (variant) {
      this._rentalFormVariantInput.value = variant.id;
    }
  }

  /* --- Helper: get selected non-rental options --- */

  _getSelectedOtherOptions() {
    this._resolveProductForm();
    if (!this._form) return {};
    const selected = {};
    const inputs = Array.from(this._form.elements).filter(el =>
      el.matches('input[data-option-position]:checked') &&
      el.getAttribute('data-option-position') !== this._rentalPosition
    );
    inputs.forEach(el => {
      selected[el.getAttribute('data-option-position')] = el.value;
    });
    return selected;
  }

  /* --- Price Display (cards) --- */

  _updatePriceDisplay() {
    if (!this._variantData.length) return;

    const selectedOther = this._getSelectedOtherOptions();
    const rentalVariants = this._variantData.filter(v => {
      if (v.isBuy) return false;
      for (const [pos, valId] of Object.entries(selectedOther)) {
        if (v.otherOptions && String(v.otherOptions[pos]) !== String(valId)) return false;
      }
      return true;
    });

    if (!rentalVariants.length) return;

    this._cards.forEach(input => {
      const days = parseInt(input.dataset.rentalPeriod, 10);
      const card = input.nextElementSibling;
      if (!card) return;

      const variant = rentalVariants.find(v => v.days === days);
      if (!variant) return;

      const priceEl = card.querySelector('.rental-card__price');
      const perDayEl = card.querySelector('.rental-card__per-day');

      if (priceEl) priceEl.textContent = formatMoney(variant.price);

      const perDay = variant.price / days;
      if (perDayEl) perDayEl.textContent = formatMoney(Math.round(perDay)) + ' / Tag';
    });
  }

  /* --- Summary Line --- */

  _updateSummary() {
    if (!this._summaryText || !this._variantData.length) return;

    const selectedOther = this._getSelectedOtherOptions();
    const rentalVariants = this._variantData.filter(v => {
      if (v.isBuy) return false;
      for (const [pos, valId] of Object.entries(selectedOther)) {
        if (v.otherOptions && String(v.otherOptions[pos]) !== String(valId)) return false;
      }
      return true;
    });

    const buyVariant = this._variantData.find(v => {
      if (!v.isBuy) return false;
      for (const [pos, valId] of Object.entries(selectedOther)) {
        if (v.otherOptions && String(v.otherOptions[pos]) !== String(valId)) return false;
      }
      return true;
    });

    const checkedCard = this.querySelector('input[data-rental-period]:checked');

    if (checkedCard) {
      const days = parseInt(checkedCard.dataset.rentalPeriod, 10);
      const variant = rentalVariants.find(v => v.days === days);
      if (variant) {
        this._summaryText.textContent = 'Jetzt mieten ab ' + formatMoney(variant.price) + ' / ' + days + ' Tage';

        if (this._savingsBadge && buyVariant) {
          const savings = buyVariant.price - variant.price;
          if (savings > 0) {
            this._savingsBadge.textContent = 'Spare ' + formatMoney(savings);
            this._savingsBadge.hidden = false;
          } else {
            this._savingsBadge.hidden = true;
          }
        }
      }
    } else {
      if (rentalVariants.length) {
        const cheapest = rentalVariants.reduce((min, v) => v.price < min.price ? v : min, rentalVariants[0]);
        this._summaryText.textContent = 'Jetzt mieten ab ' + formatMoney(cheapest.price) + ' / ' + cheapest.days + ' Tage';

        if (this._savingsBadge && buyVariant) {
          const savings = buyVariant.price - cheapest.price;
          if (savings > 0) {
            this._savingsBadge.textContent = 'Spare ' + formatMoney(savings);
            this._savingsBadge.hidden = false;
          } else {
            this._savingsBadge.hidden = true;
          }
        }
      }
    }
  }

  /* --- GESAMT Total --- */

  _updateTotal() {
    if (!this._totalPrice) return;

    const checkedCard = this.querySelector('input[data-rental-period]:checked');
    if (!checkedCard) {
      this._totalPrice.textContent = '--';
      return;
    }

    const days = parseInt(checkedCard.dataset.rentalPeriod, 10);
    const selectedOther = this._getSelectedOtherOptions();
    const variant = this._variantData.find(v => {
      if (v.isBuy || v.days !== days) return false;
      for (const [pos, valId] of Object.entries(selectedOther)) {
        if (v.otherOptions && String(v.otherOptions[pos]) !== String(valId)) return false;
      }
      return true;
    });

    this._totalPrice.textContent = variant ? formatMoney(variant.price) : '--';
  }

  /* --- Buy Price Display --- */

  _updateBuyPrice() {
    if (!this._buyPriceEl) return;

    const selectedOther = this._getSelectedOtherOptions();
    const buyVariant = this._variantData.find(v => {
      if (!v.isBuy) return false;
      for (const [pos, valId] of Object.entries(selectedOther)) {
        if (v.otherOptions && String(v.otherOptions[pos]) !== String(valId)) return false;
      }
      return true;
    });

    if (buyVariant) {
      this._buyPriceEl.textContent = formatMoney(buyVariant.price);
    }
  }
}

customElements.define('rental-period-selector', RentalPeriodSelector);

/* --------------------------------------------------------------------------
   RentalCalendar
   -------------------------------------------------------------------------- */

class RentalCalendar extends HTMLElement {
  connectedCallback() {
    this._periodDays = 4;
    this._selectedStartDate = null;
    this._currentMonth = new Date().getMonth();
    this._currentYear = new Date().getFullYear();
    this._today = new Date();
    this._today.setHours(0, 0, 0, 0);

    this._monthTitle = this.querySelector('.rental-calendar__month-title');
    this._daysContainer = this.querySelector('.rental-calendar__days');
    this._prevBtn = this.querySelector('[data-calendar-prev]');
    this._nextBtn = this.querySelector('[data-calendar-next]');
    this._startInput = this.querySelector('input[name="properties[Rental Start]"]');
    this._endInput = this.querySelector('input[name="properties[Rental End]"]');
    this._periodInput = this.querySelector('input[name="properties[_rental_period_days]"]');
    this._infoEl = this.querySelector('.rental-calendar__selected-info');

    // Parse booking availability data from parent <rental-period-selector>
    const parent = this.closest('rental-period-selector');
    const bookingScript = parent ? parent.querySelector('script[data-rental-bookings]') : null;
    if (bookingScript) {
      try {
        const data = JSON.parse(bookingScript.textContent);
        this._bookings = Array.isArray(data.bookings) ? data.bookings : [];
        this._turnaroundDays = typeof data.turnaroundDays === 'number' ? data.turnaroundDays : 3;
        this._leadDays = typeof data.leadDays === 'number' ? data.leadDays : 2;
      } catch (e) {
        this._bookings = [];
        this._turnaroundDays = 3;
        this._leadDays = 2;
      }
    } else {
      this._bookings = [];
      this._turnaroundDays = 3;
      this._leadDays = 2;
    }

    // Compute earliest start date (today + leadDays business days)
    this._earliestStartDate = this._computeEarliestStart();

    // Compute unavailable dates (no variant filter initially)
    this._variantFilter = null;
    this._unavailableDates = this._computeUnavailableDates(this._variantFilter);

    if (this._prevBtn) this._prevBtn.addEventListener('click', () => this._navigate(-1));
    if (this._nextBtn) this._nextBtn.addEventListener('click', () => this._navigate(1));

    this._render();
  }

  show() {
    this.classList.add('is-visible');
  }

  hide() {
    this.classList.remove('is-visible');
  }

  setPeriodDays(days) {
    this._periodDays = days;
    if (this._periodInput) this._periodInput.value = days;

    // Re-validate current selection — longer period may now overlap blocked dates
    if (this._selectedStartDate) {
      const overlap = this._rangeOverlapsUnavailable(this._selectedStartDate, days);
      if (overlap) {
        // Clear selection — new period overlaps bookings
        this._selectedStartDate = null;
        if (this._startInput) { this._startInput.value = ''; this._startInput.disabled = true; }
        if (this._endInput) { this._endInput.value = ''; this._endInput.disabled = true; }
        if (this._infoEl) { this._infoEl.textContent = ''; this._infoEl.hidden = true; }
      } else {
        this._setDates(this._selectedStartDate);
      }
    }
    this._render();
  }

  setVariantFilter(variantName) {
    this._variantFilter = variantName || null;
    this._unavailableDates = this._computeUnavailableDates(this._variantFilter);

    // Re-validate current selection against new availability
    if (this._selectedStartDate) {
      const overlap = this._rangeOverlapsUnavailable(this._selectedStartDate, this._periodDays);
      if (overlap) {
        this._selectedStartDate = null;
        if (this._startInput) { this._startInput.value = ''; this._startInput.disabled = true; }
        if (this._endInput) { this._endInput.value = ''; this._endInput.disabled = true; }
        if (this._infoEl) { this._infoEl.textContent = ''; this._infoEl.hidden = true; }
      }
    }
    this._render();
  }

  /**
   * Compute the earliest selectable start date: today + leadDays business days.
   */
  _computeEarliestStart() {
    const cursor = new Date(this._today);
    let counted = 0;
    while (counted < this._leadDays) {
      cursor.setDate(cursor.getDate() + 1);
      const dow = cursor.getDay();
      if (dow !== 0 && dow !== 6) counted++;
    }
    return cursor;
  }

  /**
   * Build a Set of ISO date strings that are unavailable (booked + turnaround buffer).
   * If variantFilter is set, only include bookings that match that variant or have no variant.
   */
  _computeUnavailableDates(variantFilter) {
    const unavailable = new Set();

    for (const booking of this._bookings) {
      // Filter by variant if applicable
      if (variantFilter && booking.variant && booking.variant !== variantFilter) continue;

      if (!booking.start) continue;
      const start = new Date(booking.start + 'T00:00:00');
      const cursor = new Date(start);

      if (booking.until) {
        // Shop rentals (synced by the worker): blocked until the piece is back
        // and cleaned — `until` already includes that, so no extra buffer.
        const until = new Date(booking.until + 'T00:00:00');
        while (cursor < until) {
          unavailable.add(toISODate(cursor));
          cursor.setDate(cursor.getDate() + 1);
        }
        continue;
      }

      // Manual entries: start → end inclusive, plus the turnaround buffer.
      if (!booking.end) continue;
      const end = new Date(booking.end + 'T00:00:00');
      while (cursor <= end) {
        unavailable.add(toISODate(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }

      const buffer = getBusinessDayBuffer(end, this._turnaroundDays);
      for (const d of buffer) unavailable.add(d);
    }

    return unavailable;
  }

  /**
   * Check if the rental range [startDate, startDate + periodDays) overlaps any unavailable date.
   */
  _rangeOverlapsUnavailable(startDate, periodDays) {
    const cursor = new Date(startDate);
    for (let i = 0; i < periodDays; i++) {
      if (this._unavailableDates.has(toISODate(cursor))) return true;
      cursor.setDate(cursor.getDate() + 1);
    }
    return false;
  }

  enableInputs() {
    if (this._startInput) this._startInput.disabled = false;
    if (this._endInput) this._endInput.disabled = false;
    if (this._periodInput) this._periodInput.disabled = false;
  }

  disableInputs() {
    if (this._startInput) this._startInput.disabled = true;
    if (this._endInput) this._endInput.disabled = true;
    if (this._periodInput) this._periodInput.disabled = true;
  }

  _navigate(direction) {
    this._currentMonth += direction;
    if (this._currentMonth > 11) {
      this._currentMonth = 0;
      this._currentYear++;
    } else if (this._currentMonth < 0) {
      this._currentMonth = 11;
      this._currentYear--;
    }
    this._render();
  }

  _render() {
    const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

    if (this._monthTitle) {
      this._monthTitle.textContent = months[this._currentMonth] + ' ' + this._currentYear;
    }

    if (this._prevBtn) {
      const now = new Date();
      this._prevBtn.disabled = (this._currentYear === now.getFullYear() && this._currentMonth <= now.getMonth());
    }

    if (!this._daysContainer) return;
    this._daysContainer.innerHTML = '';

    const firstDay = new Date(this._currentYear, this._currentMonth, 1);
    const lastDay = new Date(this._currentYear, this._currentMonth + 1, 0);

    let startDow = firstDay.getDay() - 1;
    if (startDow < 0) startDow = 6;

    for (let i = 0; i < startDow; i++) {
      const empty = document.createElement('span');
      this._daysContainer.appendChild(empty);
    }

    let endDate = null;
    if (this._selectedStartDate) {
      endDate = new Date(this._selectedStartDate);
      endDate.setDate(endDate.getDate() + this._periodDays);
    }

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rental-calendar__day';
      btn.textContent = day;

      const date = new Date(this._currentYear, this._currentMonth, day);
      date.setHours(0, 0, 0, 0);

      const isoDate = toISODate(date);
      const isUnavailable = this._unavailableDates.has(isoDate);
      const isBeforeEarliest = date < this._earliestStartDate;

      if (date < this._today || isBeforeEarliest) {
        btn.disabled = true;
      } else if (isUnavailable) {
        btn.disabled = true;
        btn.classList.add('is-unavailable');
      } else {
        btn.addEventListener('click', () => this._selectDate(date));
      }

      if (this._selectedStartDate && date.getTime() === this._selectedStartDate.getTime()) {
        btn.classList.add('is-selected', 'is-range-start');
      }

      if (endDate && date.getTime() === endDate.getTime()) {
        btn.classList.add('is-range-end');
      }

      if (this._selectedStartDate && endDate &&
          date > this._selectedStartDate && date < endDate) {
        btn.classList.add('is-in-range');
      }

      this._daysContainer.appendChild(btn);
    }
  }

  _selectDate(date) {
    // Validate that the entire rental range has no unavailable dates
    if (this._rangeOverlapsUnavailable(date, this._periodDays)) {
      this._showBookingError('Dieser Zeitraum überschneidet sich mit einer bestehenden Buchung.');
      return;
    }

    this._selectedStartDate = date;
    this._setDates(date);
    this._render();
  }

  _showBookingError(message) {
    const parent = this.closest('rental-period-selector') || this.parentElement;
    const existing = parent.querySelector('.rental-booking-error');
    if (existing) existing.remove();

    const msg = document.createElement('p');
    msg.className = 'rental-booking-error';
    msg.textContent = message;
    this.insertAdjacentElement('afterend', msg);
    setTimeout(() => msg.remove(), 4000);
  }

  _setDates(startDate) {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + this._periodDays);

    const formatDate = (d) => {
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return dd + '.' + mm + '.' + yyyy;
    };

    if (this._startInput) {
      this._startInput.value = formatDate(startDate);
      this._startInput.disabled = false;
    }
    if (this._endInput) {
      this._endInput.value = formatDate(endDate);
      this._endInput.disabled = false;
    }
    if (this._periodInput) {
      this._periodInput.value = this._periodDays;
      this._periodInput.disabled = false;
    }

    if (this._infoEl) {
      this._infoEl.textContent = formatDate(startDate) + ' – ' + formatDate(endDate);
      this._infoEl.hidden = false;
    }

    // Dispatch event so parent component knows a date was selected
    this.dispatchEvent(new CustomEvent('rental-calendar:date-selected', { bubbles: true }));
  }
}

customElements.define('rental-calendar', RentalCalendar);

/* one-item-per-cart enforcement: see snippets/rental-period-selector.liquid */
